/**
 * Marketing Hooks - STORY-049 to STORY-051
 *
 * React Query hooks for marketing management:
 * - Campaigns
 * - Segments
 * - Subscribers
 * - Stats
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { marketingService } from '@/services/marketing'
import type {
  CampaignFilters,
  CampaignFormData,
  SegmentFormData,
  SubscriberFilters,
  UnsubscribeFormData,
} from '@/types/marketing'

// ==================== CAMPAIGNS ====================

/**
 * Hook to fetch campaigns with filters
 */
export function useCampaigns(filters?: CampaignFilters) {
  return useQuery({
    queryKey: ['marketing-campaigns', filters],
    queryFn: () => marketingService.getCampaigns(filters),
  })
}

/**
 * Hook to fetch a single campaign
 */
export function useCampaign(id: string) {
  return useQuery({
    queryKey: ['marketing-campaign', id],
    queryFn: () => marketingService.getCampaignById(id),
    enabled: !!id,
  })
}

/**
 * Hook to create a campaign
 */
export function useCreateCampaign() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CampaignFormData) => marketingService.createCampaign(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['marketing-campaigns'] })
      queryClient.invalidateQueries({ queryKey: ['marketing-stats'] })
    },
  })
}

/**
 * Hook to update a campaign
 */
export function useUpdateCampaign() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CampaignFormData> }) =>
      marketingService.updateCampaign(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['marketing-campaigns'] })
      queryClient.invalidateQueries({ queryKey: ['marketing-campaign', id] })
    },
  })
}

/**
 * Hook to delete a campaign
 */
export function useDeleteCampaign() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => marketingService.deleteCampaign(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['marketing-campaigns'] })
      queryClient.invalidateQueries({ queryKey: ['marketing-stats'] })
    },
  })
}

/**
 * Hook to schedule a campaign
 */
export function useScheduleCampaign() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, scheduledAt }: { id: string; scheduledAt: string }) =>
      marketingService.scheduleCampaign(id, scheduledAt),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['marketing-campaigns'] })
      queryClient.invalidateQueries({ queryKey: ['marketing-campaign', id] })
      queryClient.invalidateQueries({ queryKey: ['marketing-stats'] })
    },
  })
}

/**
 * Hook to send a campaign immediately
 */
export function useSendCampaign() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => marketingService.sendCampaign(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['marketing-campaigns'] })
      queryClient.invalidateQueries({ queryKey: ['marketing-campaign', id] })
      queryClient.invalidateQueries({ queryKey: ['marketing-stats'] })
    },
  })
}

/**
 * Hook to pause a campaign
 */
export function usePauseCampaign() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => marketingService.pauseCampaign(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['marketing-campaigns'] })
      queryClient.invalidateQueries({ queryKey: ['marketing-campaign', id] })
    },
  })
}

/**
 * Hook to cancel a campaign
 */
export function useCancelCampaign() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => marketingService.cancelCampaign(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['marketing-campaigns'] })
      queryClient.invalidateQueries({ queryKey: ['marketing-campaign', id] })
      queryClient.invalidateQueries({ queryKey: ['marketing-stats'] })
    },
  })
}

/**
 * Hook to duplicate a campaign
 */
export function useDuplicateCampaign() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => marketingService.duplicateCampaign(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['marketing-campaigns'] })
    },
  })
}

// ==================== SEGMENTS ====================

/**
 * Hook to fetch all segments
 */
export function useSegments() {
  return useQuery({
    queryKey: ['marketing-segments'],
    queryFn: () => marketingService.getSegments(),
  })
}

/**
 * Hook to fetch a single segment
 */
export function useSegment(id: string) {
  return useQuery({
    queryKey: ['marketing-segment', id],
    queryFn: () => marketingService.getSegmentById(id),
    enabled: !!id,
  })
}

/**
 * Hook to create a segment
 */
export function useCreateSegment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: SegmentFormData) => marketingService.createSegment(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['marketing-segments'] })
    },
  })
}

/**
 * Hook to update a segment
 */
export function useUpdateSegment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<SegmentFormData> }) =>
      marketingService.updateSegment(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['marketing-segments'] })
      queryClient.invalidateQueries({ queryKey: ['marketing-segment', id] })
    },
  })
}

/**
 * Hook to delete a segment
 */
export function useDeleteSegment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => marketingService.deleteSegment(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['marketing-segments'] })
    },
  })
}

// ==================== SUBSCRIBERS ====================

/**
 * Hook to fetch subscribers with filters
 */
export function useSubscribers(filters?: SubscriberFilters) {
  return useQuery({
    queryKey: ['marketing-subscribers', filters],
    queryFn: () => marketingService.getSubscribers(filters),
  })
}

/**
 * Hook to fetch subscriber by token (public)
 */
export function useSubscriberByToken(token: string) {
  return useQuery({
    queryKey: ['marketing-subscriber-token', token],
    queryFn: () => marketingService.getSubscriberByToken(token),
    enabled: !!token,
  })
}

/**
 * Hook to update subscriber preferences (public)
 */
export function useUpdateSubscriberPreferences() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ email, data }: { email: string; data: UnsubscribeFormData }) =>
      marketingService.updateSubscriberPreferences(email, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['marketing-subscribers'] })
      queryClient.invalidateQueries({ queryKey: ['marketing-subscriber-token'] })
      queryClient.invalidateQueries({ queryKey: ['marketing-stats'] })
    },
  })
}

/**
 * Hook to resubscribe
 */
export function useResubscribe() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (email: string) => marketingService.resubscribe(email),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['marketing-subscribers'] })
      queryClient.invalidateQueries({ queryKey: ['marketing-subscriber-token'] })
      queryClient.invalidateQueries({ queryKey: ['marketing-stats'] })
    },
  })
}

// ==================== STATS ====================

/**
 * Hook to fetch marketing stats
 */
export function useMarketingStats() {
  return useQuery({
    queryKey: ['marketing-stats'],
    queryFn: () => marketingService.getStats(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

/**
 * Hook to fetch campaign stats
 */
export function useCampaignStats(id: string) {
  return useQuery({
    queryKey: ['marketing-campaign-stats', id],
    queryFn: () => marketingService.getCampaignStats(id),
    enabled: !!id,
  })
}
